import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';
import { useState } from 'react';
import { Nullable } from '@tcc/common';
import { useAppDispatch } from '../../../utils';
import { BASE_FIELDVIEW_URL } from '../../../constants/urls';
import { PanelCards } from '../../Panel';
import ApplicationCard from '../../../components/cards/applications/ApplicationCard';
import ApplicationPreview from '../../../components/cards/applications/ApplicationPreview';
import ApplicationsDetails from './ApplicationsDetails';
import { ApplicationsData } from '../../../types/Application';
import { setActiveSubLayer } from '../../../actions/layersActions';
import { trackEvent } from '../../../actions/analyticsActions';
import { IN_SEASON } from '../../../constants/mapLayerIds';
import { FHA, PRESCRIPTIONS, WEATHER } from '../../../constants/cards';
import {
  usernameSelector,
  userSelector,
} from '../../../selectors/userSelectors';
import { DMB_CROP_SEASON_FILTER } from '../../../constants/featureFlags';
import { getFeatureValue } from '../../../selectors/featureSelectors';
import type { FeatureFlagKey } from '../../../types/FeatureFlag';
import { FeatureFlagKey as FeatureFlagKeyEnum } from '../../../types';
import { getApplicationTreatmentsBySeason } from '../../../selectors/applicationTreatmentSelectors';
import { treatmentsToApplicationsData } from '../../../utils/applicationTreatmentTransformers';
import { AppState } from '../../../store/types/Store';
import { ApplicationsPanelV2 } from './ApplicationsPanelV2/ApplicationsPanelV2';
import { decodeFvSpatialPathSegment } from '../../../utils/fvSpatialEncoding';
import { APPLICATION_TREATMENT_DATE_FORMAT } from '../../../utils/dateHelpers';
import { getCurrentSeason } from '../../../selectors';

const CARDS = [WEATHER, PRESCRIPTIONS, FHA];
const LAYER = IN_SEASON.APPLICATIONS;
const FV_SPATIAL_BASE_IMAGE_URL_V2 = `${BASE_FIELDVIEW_URL}/harvest/geo/v2/thumbnail`;
const THUMBNAIL_DIM = 70;

interface Application {
  liquidYear: number;
  crop: string;
  cropName: string;
  liquidEndDate: number;
  liquidStartDate: number;
  seasonTag?: string;
}

interface ActiveApplication {
  crop: string;
  endDate: number;
}

interface User {
  uuid: string;
}

interface ApplicationsPanelProps {
  activeApplication: ActiveApplication;
  applications?: Application[];
  collapsePanel: () => void;
  fieldUUID: string;
  index: number;
  isCollapsed: boolean;
  isSplitViewEnabled: boolean;
  locale: string;
  season: string;
  setActiveSubLayer: (
    layer: string,
    activeSubLayerId: string,
    index: number,
    season: string
  ) => void;
  username: string;
  user: User | null;
  isCropSeasonFilterEnabled: boolean;
  isApplicationCardEnabled: boolean;
  applicationsData: ApplicationsData;
  dispatch: Dispatch;
}

interface RenderFirstCardParams {
  applicationInfo: Application | undefined;
  applicationsData: ApplicationsData;
  expandedApplication: Nullable<string>;
  season: string;
  applications: Application[];
}

const getApplicationIndex = (
  activeApplication: ActiveApplication,
  applications: Application[]
) =>
  applications.findIndex(
    ({ crop, liquidEndDate }) =>
      activeApplication.crop === crop &&
      activeApplication.endDate === liquidEndDate
  );

export const ApplicationsPanel = ({
  locale,
  index,
  season,
  setActiveSubLayer,
  collapsePanel,
  activeApplication,
  fieldUUID,
  user,
  isApplicationCardEnabled,
  isSplitViewEnabled,
  isCollapsed,
  applications = [],
  isCropSeasonFilterEnabled,
  applicationsData,
}: ApplicationsPanelProps) => {
  // activeApplication.crop is double-encoded (for fv-spatial tile URLs).
  // ApplicationsDetails builds its applicationKey with single-encoded name via encodeURIComponent(name).
  // Decode once so the initial expandedApplication matches the applicationKey format.
  const [expandedApplication, setExpandedApplication] =
    useState<Nullable<string>>(activeApplication ? `${activeApplication.endDate}_${decodeFvSpatialPathSegment(activeApplication.crop)}` : null);

  const dispatch = useAppDispatch();

  const handleToggleApplicationExpand = (applicationKey: Nullable<string>) => {
    if (applicationKey !== expandedApplication) {
      setExpandedApplication(applicationKey);
      dispatch(
        trackEvent('DMB - Individual Application Expanded', {
          applicationKey,
        })
      );
    } else {
      setExpandedApplication(null);
    }
  };

  const handleClick = (crop: string, endDate: number) => {
    const activeSubLayerId = `${endDate}_${crop}`;
    setActiveSubLayer(LAYER, activeSubLayerId, index, season);
    collapsePanel();
  };

  const paintApplication = (application: Application) => {
    const { liquidYear, crop, cropName, liquidEndDate, liquidStartDate } =
      application;
    const active =
      activeApplication.crop === crop &&
      activeApplication.endDate === liquidEndDate;
    const date = getDate(liquidEndDate);

    if (!user || !user.uuid) {
      return null;
    }

    const lastPartURL = `?startTime=${liquidStartDate}&endTime=${liquidEndDate}`;
    const firstPartURL = `${FV_SPATIAL_BASE_IMAGE_URL_V2}/${user.uuid}/${fieldUUID}/${liquidYear}/${crop}/${LAYER}/${THUMBNAIL_DIM}.png`;
    const url = firstPartURL + lastPartURL;

    return (
      <ApplicationCard
        active={active}
        formattedDate={date}
        id={crop}
        key={`application-card-${crop}`}
        name={cropName}
        onClick={() => handleClick(crop, liquidEndDate)}
        testId={`application-card-${cropName}`}
        url={url}
      />
    );
  };

  const renderFirstCard = ({
    applicationInfo,
    applicationsData,
    expandedApplication,
    season,
    applications,
  }: RenderFirstCardParams) => {
    if (isSplitViewEnabled && isCollapsed) {
      return (
        <ApplicationPreview
          activeApplicationName={applicationInfo && applicationInfo.cropName}
          decrementActiveApplication={decrementActiveApplication}
          incrementActiveApplication={incrementActiveApplication}
        />
      );
    }

    if (isApplicationCardEnabled) {
      return (
        <ApplicationsDetails
          applicationsData={applicationsData}
          testId="applications-panel"
          expandedApplication={expandedApplication}
          onToggleApplicationExpand={handleToggleApplicationExpand}
          season={season}
          locale={locale}
        />
      );
    }

    return (
      <div data-test-id="paintApplication" key={`applications-${season}`}>
        {applications?.map(paintApplication)}
      </div>
    );
  };

  const getDate = (date: number) => {
    const dateOpts: Intl.DateTimeFormatOptions = APPLICATION_TREATMENT_DATE_FORMAT;

    return new Date(date).toLocaleDateString(locale, dateOpts);
  };

  const decrementActiveApplication = () => {
    const applicationIndex = getApplicationIndex(
      activeApplication,
      applications
    );
    if (applicationIndex > -1) {
      const newIndex =
        applicationIndex === 0 ? applications.length - 1 : applicationIndex - 1;
      setActiveSubLayer(
        LAYER,
        `${applications[newIndex].liquidEndDate}_${applications[newIndex].crop}`,
        index,
        season
      );
    }
  };

  const incrementActiveApplication = () => {
    const applicationIndex = getApplicationIndex(
      activeApplication,
      applications
    );
    if (applicationIndex > -1) {
      const newIndex =
        applicationIndex >= applications.length - 1 ? 0 : applicationIndex + 1;
      setActiveSubLayer(
        LAYER,
        `${applications[newIndex].liquidEndDate}_${applications[newIndex].crop}`,
        index,
        season
      );
    }
  };

  if (isApplicationCardEnabled) {
    return (
      <ApplicationsPanelV2
        activeApplication={activeApplication}
        collapsePanel={collapsePanel}
        index={index}
        isCollapsed={isCollapsed}
        isSplitViewEnabled={isSplitViewEnabled}
        locale={locale}
        season={season}
        setActiveSubLayer={setActiveSubLayer}
        setExpandedApplication={handleToggleApplicationExpand}
        expandedApplication={expandedApplication}
      />
    );
  }

  // --- FEATURE FLAG "DMB_CROP_SEASON_FILTER": Filter applications by crop season  ---
  if (isCropSeasonFilterEnabled && season) {
    const filteredApplications = applications.filter((app) =>
      app.seasonTag ? app.seasonTag === season : false
    );

    const applicationInfo = filteredApplications.find(
      ({ crop: appCrop, liquidEndDate }) =>
        activeApplication.crop === appCrop &&
        activeApplication.endDate === liquidEndDate
    );
    const activeApplicationDate =
      applicationInfo && getDate(applicationInfo.liquidEndDate);

    const renderApplicationsData = isApplicationCardEnabled
      ? applicationsData
      : { applications: [] };

    const firstCard = renderFirstCard({
      applicationInfo,
      applicationsData: renderApplicationsData,
      expandedApplication,
      season,
      applications: filteredApplications,
    });

    return (
      <PanelCards
        cards={CARDS}
        firstCard={firstCard || undefined}
        headerDetail={activeApplicationDate}
        index={index}
        isCollapsed={isCollapsed}
        testId="application"
      />
    );
  }
  // --- END FEATURE FLAG ---

  // Old code with no crop filtering
  const applicationInfo = applications.find(
    ({ crop, liquidEndDate }) =>
      activeApplication.crop === crop &&
      activeApplication.endDate === liquidEndDate
  );
  const activeApplicationDate =
    applicationInfo && getDate(applicationInfo.liquidEndDate);

  const renderApplicationsData = isApplicationCardEnabled
    ? applicationsData
    : { applications: [] };

  const firstCard = renderFirstCard({
    applicationInfo,
    applicationsData: renderApplicationsData,
    expandedApplication,
    season,
    applications,
  });

  return (
    <PanelCards
      cards={CARDS}
      firstCard={firstCard || undefined}
      headerDetail={activeApplicationDate}
      index={index}
      isCollapsed={isCollapsed}
      testId="application"
    />
  );
};

interface OwnProps {
  index: number;
}

export function mapStateToProps(state: AppState, props: OwnProps) {
  const activeSeason = getCurrentSeason(state, props);
  const liquidLayer = state.layers.list?.[activeSeason]?.[LAYER] as {
    activeSubLayerIds?: string[];
  } | undefined;
  const activeSubLayerId = liquidLayer?.activeSubLayerIds?.[props.index] || '';
  const splitIndex = activeSubLayerId.indexOf('_');
  const activeEndDate = parseInt(activeSubLayerId.slice(0, splitIndex), 10);
  const activeCrop = activeSubLayerId.slice(splitIndex + 1);

  const isApplicationCardEnabled = getFeatureValue(state, {
    key: FeatureFlagKeyEnum.DMB_APPLICATION_CARD,
  });
  const user = userSelector(state);
  const fieldUUID = state.field.uuid?.toUpperCase() ?? '';
  const locale = state.app.locale ?? 'en-us';

  let applicationsData: ApplicationsData = { applications: [] };
  if (isApplicationCardEnabled && activeSeason) {
    const treatments = getApplicationTreatmentsBySeason(state, activeSeason);

    applicationsData = treatmentsToApplicationsData(treatments, {
      userUuid: user?.uuid ?? '',
      fieldUUID,
    });
  }

  return {
    activeApplication: { crop: activeCrop, endDate: activeEndDate },
    applications: state.seasons.list?.[activeSeason]?.liquidData || [],
    fieldUUID,
    locale,
    season: activeSeason,
    username: usernameSelector(state),
    user,
    isCropSeasonFilterEnabled: getFeatureValue(state, {
      key: DMB_CROP_SEASON_FILTER as FeatureFlagKey,
    }),
    isApplicationCardEnabled,
    applicationsData,
  };
}

function mapDispatchToProps(dispatch: Dispatch) {
  return {
    dispatch,
    ...bindActionCreators(
      {
        setActiveSubLayer,
      },
      dispatch
    ),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(ApplicationsPanel);
